import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, FolderKanban, CheckSquare, FileText, Users, Activity, LogOut,
  Workflow, Bell, Brain, BarChart3, Monitor, Command, X, MessageCircle, Home,
  Library, HeartPulse, Shield, Calculator, Settings, FolderOpen, CalendarClock, Calendar, CalendarDays,
  FileImage, Grid2X2, Sparkles, Wallet, ChevronDown, CreditCard, Building2,
  Briefcase, ClipboardList, AlertTriangle, Scale, TrendingUp, ArrowRightLeft,
  HardDrive, GitPullRequest, Megaphone, Camera, Landmark, Cake, Newspaper, FileSignature, UserPlus, LayoutTemplate, FileCheck, FileEdit, Package, Wifi, Leaf, KeyRound, ExternalLink, Target, Cloud, BookOpen,
  ClipboardCheck, ListTodo, FileStack, GitCompareArrows, Zap, Search, AlarmClock, Mail, MapPin, CalendarCheck, Star, Layers, Award, Database, Ruler, ListChecks, Trash2, HardHat, ShieldCheck
} from 'lucide-react';
import api, { getToken } from '../utils/api';
import { toast } from 'sonner';
import NotificationBell from './NotificationBell';
import UniversalSearch from './UniversalSearch';
import { hasModuleAccess, FULL_ACCESS, VIEW_ONLY } from '../utils/accessControl';

const Sidebar = ({ user, accessProfile, onLogout, onImpersonate, isOpen, onClose }) => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState({
    tasks: true,
    masters: true,
    management: true,
    project: true,
    hr: true,
    accounts: true,
    legal: true,
    adminOps: true,
    admin: true,
    sales: true,
  });

  // Pending approvals badge — polls every 60s
  const [pendingCounts, setPendingCounts] = useState({ leaves: 0, overtime: 0, sales: 0, generic: 0, total: 0 });
  useEffect(() => {
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const token = getToken();
        if (!token) return;
        const apiBase = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${apiBase}/api/leave-management/my-pending-approvals-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok && !cancelled) setPendingCounts(await res.json());
      } catch { /* silent */ }
    };
    fetchCounts();
    const id = setInterval(fetchCounts, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const handleLogout = async () => {
    try {
      await api.logout();
      onLogout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      onLogout();
      navigate('/login');
    }
  };

  const handleNavClick = () => {
    if (window.innerWidth <= 768) onClose?.();
  };

  const toggleGroup = (group) => {
    setCollapsed(prev => {
      const allCollapsed = {
        tasks: true,
        masters: true,
        management: true,
        project: true,
        hr: true,
        accounts: true,
        legal: true,
        adminOps: true,
        admin: true,
        sales: true,
      };
      // If already open, just close it. Otherwise close all and open this one.
      if (!prev[group]) {
        return { ...allCollapsed };
      }
      return { ...allCollapsed, [group]: false };
    });
  };

  const userRole = user?.role_name || user?.role || '';
  const isManager = userRole.toLowerCase().includes('admin') || userRole.toLowerCase().includes('administrator') || userRole.toLowerCase().includes('manager') || userRole.toLowerCase().includes('management') || userRole.toLowerCase().includes('director');
  const isAdmin = userRole.toLowerCase() === 'admin' || userRole.toLowerCase() === 'administrator';
  const isAdministrator = userRole.toLowerCase() === 'administrator';
  const isPartner = userRole.toLowerCase() === 'partner';
  const isOfficeSupport = ['office boy', 'office assistant'].includes(userRole.toLowerCase());
  const dept = (user?.department || '').toLowerCase();

  // Auto-expand admin sections for admin users
  useEffect(() => {
    if (isAdmin) {
      setCollapsed(prev => ({ ...prev, admin: false, adminOps: false }));
    }
  }, [isAdmin]);

  const canAccess = (module, level = VIEW_ONLY) => {
    if (isAdmin) return true;
    if (!accessProfile) return false;
    return hasModuleAccess(accessProfile, module, level);
  };

  // Check if user has custom tab access for a specific route
  const customTabs = accessProfile?.custom_tab_access || [];
  const hasCustomAccess = (route) => customTabs.includes(route);

  const NavItem = ({ to, icon, label, testId, badge }) => (
    <NavLink
      to={to}
      className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
      data-testid={testId}
      onClick={handleNavClick}
    >
      {icon}
      <span className="flex-1">{label}</span>
      {badge > 0 && (
        <span
          className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none"
          data-testid={`${testId}-badge`}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </NavLink>
  );

  const SectionHeader = ({ label, group, badge }) => (
    <button
      onClick={() => toggleGroup(group)}
      className="w-full flex items-center justify-between px-6 py-2 mt-3 mb-0.5 text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold hover:text-slate-300 transition-colors"
      data-testid={`section-${group}`}
    >
      <span className="flex items-center gap-2">
        {label}
        {/* Roll-up badge — only visible while the section is collapsed so the count
            doesn't double up with the per-item badges underneath. */}
        {badge > 0 && collapsed[group] && (
          <span
            className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold leading-none normal-case tracking-normal"
            data-testid={`section-${group}-badge`}
          >
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </span>
      <ChevronDown className={`w-3 h-3 transition-transform ${collapsed[group] ? '-rotate-90' : ''}`} />
    </button>
  );

  return (
    <>
      {isOpen && (
        <div className="sidebar-overlay md:hidden" onClick={onClose} data-testid="sidebar-overlay" />
      )}

      <div className={`sidebar ${isOpen ? 'open' : ''} flex flex-col h-screen`} data-testid="sidebar">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-500 hover:text-white md:hidden"
          data-testid="close-sidebar-btn"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 border-b border-slate-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <img src="/nirman-logo.png" alt="Nirman" className="w-11 h-11 rounded-sm object-contain brightness-0 invert" />
              <h1 className="text-base font-bold leading-tight">NIRMAN<br/>WORKHUB</h1>
            </div>
            <div className="hidden md:block">
              <NotificationBell user={user} dropdownAlign="left" />
            </div>
          </div>
          <div className="mt-3">
            <UniversalSearch />
          </div>
          <p className="text-xs text-slate-500 mt-2 uppercase tracking-wide">{user?.department}</p>
        </div>

        <nav className="py-2 overflow-y-auto flex-1 min-h-0">

          {/* ── COMMON ── */}
          <NavItem
            to={isManager ? '/dashboard/management' : (isPartner ? '/partner-portal' : '/dashboard/employee')}
            icon={<LayoutDashboard className="w-4 h-4 mr-3 flex-shrink-0" />}
            label={isPartner ? "Partner Portal" : "Dashboard"}
            testId="nav-dashboard"
          />
          {!isPartner && (
            <NavItem to="/user-guide" icon={<BookOpen className="w-4 h-4 mr-3 flex-shrink-0" />} label="User Guide" testId="nav-user-guide" />
          )}

          {/* Attendance — hidden for Partner logins (they don't check into
              sites; partners are external collaborators). Jul 2026 request. */}
          {!isPartner && (
            <NavItem to="/attendance/checkin" icon={<MapPin className="w-4 h-4 mr-3 flex-shrink-0" />} label="Site Attendance" testId="nav-attendance-checkin" />
          )}

          {/* Office Support (Office Boy / Office Assistant) only sees Dashboard, Attendance, Leave */}
          {!isOfficeSupport && <>
          {/* My Tasks, Notifications, Messages, AI Assistant accessible via dashboard */}
          {canAccess('dept_templates') && (
            <NavItem to="/department-docs" icon={<FolderOpen className="w-4 h-4 mr-3 flex-shrink-0" />} label="Dept. Templates" testId="nav-department-docs" />
          )}

          {/* ── DOCUMENT LIBRARY ── */}
          <NavItem to="/docs" icon={<FileText className="w-4 h-4 mr-3 flex-shrink-0" />} label="Documents" testId="nav-docs" />

          {/* ── PRESENTATION BUILDER (AI) — internal only ── */}
          {!isPartner && (
            <NavItem to="/presentations" icon={<Sparkles className="w-4 h-4 mr-3 flex-shrink-0" />} label="Presentation Builder" testId="nav-presentations" />
          )}

          {/* ── REMINDERS — internal only ── */}
          {!isPartner && (
            <NavItem to="/alerts" icon={<AlarmClock className="w-4 h-4 mr-3 flex-shrink-0" />} label="Reminders" testId="nav-reminders" />
          )}

          {/* ── TASKS — internal only ── */}
          {!isPartner && (
          <>
            <SectionHeader label="Tasks" group="tasks" />
            {!collapsed.tasks && (
              <>
                <NavItem to="/my-tasks" icon={<ClipboardCheck className="w-4 h-4 mr-3 flex-shrink-0" />} label="My Tasks" testId="nav-my-tasks" />
                {(isManager || hasCustomAccess('/tasks')) && (
                  <NavItem to="/tasks" icon={<ListTodo className="w-4 h-4 mr-3 flex-shrink-0" />} label="All Tasks" testId="nav-all-tasks" />
                )}
                <NavItem to="/meetings" icon={<FileText className="w-4 h-4 mr-3 flex-shrink-0" />} label="Minutes of Meeting" testId="nav-meetings" />
                <NavItem to="/templates" icon={<FileStack className="w-4 h-4 mr-3 flex-shrink-0" />} label="Templates" testId="nav-templates" />
              </>
            )}
          </>
          )}

          {/* ── MASTERS ── */}
          {((isManager && !isPartner) || hasCustomAccess('/masters')) && (
            <>
              <SectionHeader label="Masters" group="masters" />
              {!collapsed.masters && (
                <>
                  <NavItem to="/masters" icon={<Database className="w-4 h-4 mr-3 flex-shrink-0" />} label="All Masters" testId="nav-masters" />
                  {(isManager || hasCustomAccess('/partners')) && (
                    <NavItem to="/partners" icon={<Users className="w-4 h-4 mr-3 flex-shrink-0" />} label="Partners" testId="nav-partners-masters" />
                  )}
                  {(isManager || hasCustomAccess('/companies')) && (
                    <NavItem to="/companies" icon={<Building2 className="w-4 h-4 mr-3 flex-shrink-0" />} label="Company Master" testId="nav-companies-masters" />
                  )}
                  {isManager && (
                    <NavItem to="/project-statuses" icon={<Layers className="w-4 h-4 mr-3 flex-shrink-0" />} label="Project Statuses" testId="nav-project-statuses" />
                  )}
                  {isManager && (
                    <NavItem to="/leave-approval-matrix" icon={<Workflow className="w-4 h-4 mr-3 flex-shrink-0" />} label="Leave Approval Matrix" testId="nav-leave-approval-matrix" />
                  )}
                </>
              )}
            </>
          )}

          {/* ── MANAGEMENT ── */}
          {(isManager || customTabs.some(t => ['/ai-insights','/reports','/workflows','/command-centre','/desktop-activity','/decisions','/activity','/cashflow','/redevelopment'].includes(t))) && !isPartner && (
            <>
              <SectionHeader label="Management" group="management" />
              {!collapsed.management && (
                <>
                  {(canAccess('ai_insights') || hasCustomAccess('/ai-insights')) && (
                    <NavItem to="/ai-insights" icon={<Brain className="w-4 h-4 mr-3 flex-shrink-0" />} label="AI Insights" testId="nav-ai-insights" />
                  )}
                  {(canAccess('all_reports') || hasCustomAccess('/reports')) && (
                    <NavItem to="/reports" icon={<BarChart3 className="w-4 h-4 mr-3 flex-shrink-0" />} label="Reports" testId="nav-reports" />
                  )}
                  {(canAccess('workflows') || hasCustomAccess('/workflows')) && (
                    <NavItem to="/workflows" icon={<Workflow className="w-4 h-4 mr-3 flex-shrink-0" />} label="Workflows" testId="nav-workflows" />
                  )}
                  {(canAccess('command_centre') || hasCustomAccess('/command-centre')) && (
                    <NavItem to="/command-centre" icon={<Command className="w-4 h-4 mr-3 flex-shrink-0" />} label="Daily Operations" testId="nav-command-centre" />
                  )}
                  {(canAccess('desktop_activity') || hasCustomAccess('/desktop-activity')) && (
                    <NavItem to="/desktop-activity" icon={<Monitor className="w-4 h-4 mr-3 flex-shrink-0" />} label="Desktop Tracking" testId="nav-desktop-activity" />
                  )}
                  {(isManager || hasCustomAccess('/decisions')) && (
                    <NavItem to="/decisions" icon={<GitPullRequest className="w-4 h-4 mr-3 flex-shrink-0" />} label="Decisions" testId="nav-decisions" />
                  )}
                  {(isManager || hasCustomAccess('/cashflow')) && (
                    <NavItem to="/cashflow" icon={<TrendingUp className="w-4 h-4 mr-3 flex-shrink-0" />} label="Cash Flow" testId="nav-cashflow" />
                  )}
                  {(isManager || canAccess('legal') || dept === 'legal' || hasCustomAccess('/redevelopment')) && (
                    <NavItem to="/redevelopment" icon={<Landmark className="w-4 h-4 mr-3 flex-shrink-0" />} label="Redevelopment" testId="nav-redevelopment" />
                  )}
                  {isManager && (
                    <NavItem to="/approvals" icon={<CheckSquare className="w-4 h-4 mr-3 flex-shrink-0" />} label="Approvals" testId="nav-approvals" badge={pendingCounts.generic} />
                  )}
                </>
              )}
            </>
          )}

          {/* ── PROJECT ── */}
          <SectionHeader label="Project" group="project" />
          {!collapsed.project && (
            <>
              {(canAccess('projects') || isPartner || hasCustomAccess('/projects')) && (
                <NavItem to="/masters" icon={<FolderKanban className="w-4 h-4 mr-3 flex-shrink-0" />} label="Projects" testId="nav-projects" />
              )}
              {(canAccess('project_schedule') || hasCustomAccess('/project-schedule')) && (
                <NavItem to="/project-schedule" icon={<CalendarClock className="w-4 h-4 mr-3 flex-shrink-0" />} label="Project Schedule" testId="nav-project-schedule" />
              )}
              {/* Project Timeline (task gantt) — moved from Tasks section into
                  Project (Jun 2026 user request). Internal users only. */}
              {!isPartner && (
                <NavItem to="/task-gantt" icon={<BarChart3 className="w-4 h-4 mr-3 flex-shrink-0" />} label="Project Timeline" testId="nav-task-gantt" />
              )}
              {/* Project Milestones — moved out of the Legal Suits tab set
                  (Feb 2026, user request). Uses the same LegalMilestonesOverview
                  component under a new route so nothing else has to change. */}
              {(isManager || hasCustomAccess('/project-milestones')) && (
                <NavItem to="/project-milestones" icon={<ListChecks className="w-4 h-4 mr-3 flex-shrink-0" />} label="Project Milestones" testId="nav-project-milestones" />
              )}
              {(isManager || hasCustomAccess('/drawing-requirements')) && (
                <NavItem to="/drawing-requirements" icon={<Ruler className="w-4 h-4 mr-3 flex-shrink-0" />} label="Drawing Requirements" testId="nav-drawing-requirements" />
              )}
              {(isAdmin || hasCustomAccess('/drawing-library')) && (
                <NavItem to="/drawing-library" icon={<FileStack className="w-4 h-4 mr-3 flex-shrink-0" />} label="Drawing Library" testId="nav-drawing-library" />
              )}
              {!isOfficeSupport && (
                <NavItem to="/daily-reports" icon={<ClipboardList className="w-4 h-4 mr-3 flex-shrink-0" />} label="Daily Reports" testId="nav-daily-reports" />
              )}
              <NavItem to="/site-albums" icon={<Camera className="w-4 h-4 mr-3 flex-shrink-0" />} label="Site Photos" testId="nav-site-albums" />
              {isPartner && (
                <NavItem to="/documents" icon={<FileText className="w-4 h-4 mr-3 flex-shrink-0" />} label="Documents" testId="nav-documents-partner" />
              )}
              {(isManager || hasCustomAccess('/contracts')) && (
                <NavItem to="/contracts" icon={<FileSignature className="w-4 h-4 mr-3 flex-shrink-0" />} label="AMC Contracts" testId="nav-contracts" />
              )}
              {(isManager || hasCustomAccess('/work-orders')) && (
                <NavItem to="/work-orders" icon={<ClipboardList className="w-4 h-4 mr-3 flex-shrink-0" />} label="Work Orders" testId="nav-work-orders" />
              )}
              {/* Vendors & Consultants — moved from Admin to Project (Feb 2026
                  user request). Same gating as AMC Contracts / Work Orders so
                  every project manager sees it. */}
              {(isManager || hasCustomAccess('/vendors')) && (
                <NavItem to="/vendors" icon={<Package className="w-4 h-4 mr-3 flex-shrink-0" />} label="Vendors & Consultants" testId="nav-vendors" />
              )}
              {/* Contractor Insurance — moved from Admin to Project (Feb 2026
                  user request). Broadened gating (Feb 2026 follow-up) so managers
                  and custom-access users can see it without relying on the
                  legacy `insurance` module flag. */}
              {(isManager || canAccess('insurance') || hasCustomAccess('/contractor-insurance')) && (
                <NavItem to="/contractor-insurance" icon={<Shield className="w-4 h-4 mr-3 flex-shrink-0" />} label="Contractor Insurance" testId="nav-contractor-insurance" />
              )}
              {(isManager || hasCustomAccess('/rera-compliance')) && (
                <NavItem to="/rera-compliance" icon={<Shield className="w-4 h-4 mr-3 flex-shrink-0" />} label="RERA Compliance" testId="nav-rera-compliance" />
              )}
              {(isManager || hasCustomAccess('/competitor-rera')) && (
                <NavItem to="/competitor-rera" icon={<Shield className="w-4 h-4 mr-3 flex-shrink-0" />} label="Competitor RERA" testId="nav-competitor-rera" />
              )}
              {(isManager || hasCustomAccess('/green-building')) && (
                <NavItem to="/green-building" icon={<Leaf className="w-4 h-4 mr-3 flex-shrink-0" />} label="Green Building" testId="nav-green-building" />
              )}
              {(isManager || hasCustomAccess('/labour-licences')) && (
                <NavItem to="/labour-licences" icon={<HardHat className="w-4 h-4 mr-3 flex-shrink-0" />} label="Labour Licences · BOCW" testId="nav-labour-licences" />
              )}
              {(isManager || isPartner || hasCustomAccess('/noc-sanctions')) && (
                <NavItem to="/noc-sanctions" icon={<FileCheck className="w-4 h-4 mr-3 flex-shrink-0" />} label="NOC & Sanctions" testId="nav-noc-sanctions" />
              )}
              {(isManager || hasCustomAccess('/flat-changes')) && (
                <NavItem to="/flat-changes" icon={<FileEdit className="w-4 h-4 mr-3 flex-shrink-0" />} label="Changes" testId="nav-flat-changes" />
              )}
              {(isManager || isPartner || hasCustomAccess('/compliance-tracker')) && (
                <NavItem to="/compliance-tracker" icon={<BarChart3 className="w-4 h-4 mr-3 flex-shrink-0" />} label="Compliance Tracker" testId="nav-compliance-tracker" />
              )}
              {(isManager || hasCustomAccess('/noc-requirements')) && (
                <NavItem to="/noc-requirements" icon={<ShieldCheck className="w-4 h-4 mr-3 flex-shrink-0" />} label="NOC Requirements" testId="nav-noc-requirements" />
              )}
              {isPartner && (
                <NavItem to="/redevelopment" icon={<Landmark className="w-4 h-4 mr-3 flex-shrink-0" />} label="Redevelopment" testId="nav-redevelopment-partner" />
              )}
              {(canAccess('task_library') || hasCustomAccess('/task-library')) && (
                <NavItem to="/templates?tab=library" icon={<Library className="w-4 h-4 mr-3 flex-shrink-0" />} label="Task Library" testId="nav-task-library" />
              )}
              {(isManager || hasCustomAccess('/project-comparison')) && (
                <NavItem to="/project-comparison" icon={<GitCompareArrows className="w-4 h-4 mr-3 flex-shrink-0" />} label="Compare Projects" testId="nav-project-comparison" />
              )}
              {isManager && (
                <NavItem to="/correspondence" icon={<Mail className="w-4 h-4 mr-3 flex-shrink-0" />} label="Correspondence" testId="nav-correspondence" />
              )}
              <NavItem to="/petty-cash" icon={<Wallet className="w-4 h-4 mr-3 flex-shrink-0" />} label="Petty Cash" testId="nav-petty-cash" />
            </>
          )}

          </>}
          {/* End of !isOfficeSupport wrapper */}

          {/* ── HR ── */}
          {!isPartner && (
          <>
          {!isOfficeSupport && <SectionHeader label="HR" group="hr" badge={pendingCounts.leaves + pendingCounts.overtime} />}
          {isOfficeSupport ? (
            /* Office support only sees Leave & Attendance */
            <NavItem to="/leave-management" icon={<CalendarClock className="w-4 h-4 mr-3 flex-shrink-0" />} label="Leave & Attendance" testId="nav-leave-management" />
          ) : !collapsed.hr && (
            <>
              {((isManager && canAccess('employees')) || hasCustomAccess('/employees')) && (
                <NavItem to="/employees" icon={<Users className="w-4 h-4 mr-3 flex-shrink-0" />} label="Employees" testId="nav-employees" />
              )}
              {((isManager && canAccess('departments')) || hasCustomAccess('/departments')) && (
                <NavItem to="/departments" icon={<Building2 className="w-4 h-4 mr-3 flex-shrink-0" />} label="Departments" testId="nav-departments" />
              )}
              {((isManager && canAccess('roles')) || hasCustomAccess('/roles')) && (
                <NavItem to="/roles" icon={<Shield className="w-4 h-4 mr-3 flex-shrink-0" />} label="Roles" testId="nav-roles" />
              )}
              {(isManager || hasCustomAccess('/employee-matrix')) && (
                <NavItem to="/employee-matrix" icon={<Grid2X2 className="w-4 h-4 mr-3 flex-shrink-0" />} label="Employee Matrix" testId="nav-employee-matrix" />
              )}
              {(isAdmin || canAccess('hr') || hasCustomAccess('/hr')) && (
                <NavItem to="/hr" icon={<Briefcase className="w-4 h-4 mr-3 flex-shrink-0" />} label="HR / Recruitment" testId="nav-hr" />
              )}
              {(isManager || hasCustomAccess('/greetings')) && (
                <NavItem to="/greetings" icon={<Cake className="w-4 h-4 mr-3 flex-shrink-0" />} label="Greetings" testId="nav-greetings" />
              )}
              <NavItem to="/leave-management" icon={<CalendarClock className="w-4 h-4 mr-3 flex-shrink-0" />} label="Leave & Attendance" testId="nav-leave-management" />
              {isManager && (
                <NavItem to="/leave-approvals" icon={<CalendarCheck className="w-4 h-4 mr-3 flex-shrink-0" />} label="Leave Approvals" testId="nav-leave-approvals" badge={pendingCounts.leaves + pendingCounts.overtime} />
              )}
              {(isAdmin
                || (user?.department || '').toLowerCase().includes('hr')
                || (user?.role_name || '').toLowerCase().includes('hr')
                || (user?.role || '').toLowerCase().includes('hr')
                || hasCustomAccess('/attendance')) && (
                <NavItem to="/attendance" icon={<MapPin className="w-4 h-4 mr-3 flex-shrink-0" />} label="Attendance Admin" testId="nav-attendance-admin" />
              )}
              {isManager && (
                <NavItem to="/hr-dashboard" icon={<Activity className="w-4 h-4 mr-3 flex-shrink-0" />} label="HR Dashboard" testId="nav-hr-dashboard" />
              )}
              <NavItem to="/company-calendar" icon={<CalendarDays className="w-4 h-4 mr-3 flex-shrink-0" />} label="Company Calendar" testId="nav-company-calendar" />
              {(isManager || hasCustomAccess('/payroll')) && (
                <NavItem to="/payroll" icon={<Calculator className="w-4 h-4 mr-3 flex-shrink-0" />} label="Payroll" testId="nav-payroll" />
              )}
              {isManager && (
                <>
                  <NavItem to="/experience-letters" icon={<Award className="w-4 h-4 mr-3 flex-shrink-0" />} label="Experience Letters" testId="nav-experience-letters" />
                  <NavItem to="/appointment-letters" icon={<FileText className="w-4 h-4 mr-3 flex-shrink-0" />} label="Appointment Letters" testId="nav-appointment-letters" />
                </>
              )}
            </>
          )}
          </>
          )}

          {/* ── ACCOUNTS ── */}
          {!isPartner && !isOfficeSupport && (
          <>
          <SectionHeader label="Accounts" group="accounts" />
          {!collapsed.accounts && (
            <>
              {(isManager || canAccess('budget') || hasCustomAccess('/budget')) && (
                <NavItem to="/budget" icon={<Wallet className="w-4 h-4 mr-3 flex-shrink-0" />} label="Budget" testId="nav-budget" />
              )}
              {(isAdmin || canAccess('leased_assets') || hasCustomAccess('/leased-assets')) && (
                <NavItem to="/leased-assets" icon={<Building2 className="w-4 h-4 mr-3 flex-shrink-0" />} label="Leased Assets" testId="nav-leased-assets" />
              )}
              {(isAdmin || canAccess('credit_cards') || hasCustomAccess('/credit-cards')) && (
                <NavItem to="/alerts?tab=credit-cards" icon={<CreditCard className="w-4 h-4 mr-3 flex-shrink-0" />} label="Credit Cards" testId="nav-credit-cards" />
              )}
              {(isManager || hasCustomAccess('/property-tax')) && (
                <NavItem to="/property-tax" icon={<Landmark className="w-4 h-4 mr-3 flex-shrink-0" />} label="Property Tax" testId="nav-property-tax" />
              )}
              {(isManager || hasCustomAccess('/companies')) && (
                <NavItem to="/companies" icon={<Building2 className="w-4 h-4 mr-3 flex-shrink-0" />} label="Company Master" testId="nav-companies" />
              )}
              {(isManager || hasCustomAccess('/partners')) && (
                <NavItem to="/partners" icon={<Users className="w-4 h-4 mr-3 flex-shrink-0" />} label="Partners" testId="nav-partners" />
              )}
              {(isManager || hasCustomAccess('/financial-documents')) && (
                <NavItem to="/financial-documents" icon={<FolderOpen className="w-4 h-4 mr-3 flex-shrink-0" />} label="Financial Docs" testId="nav-financial-docs" />
              )}
              {(canAccess('compliance') || hasCustomAccess('/compliance-alerts')) && (
                <NavItem to="/compliance-alerts" icon={<Calculator className="w-4 h-4 mr-3 flex-shrink-0" />} label="Compliance Alerts" testId="nav-compliance-alerts" />
              )}
              {(isManager || canAccess('budget') || hasCustomAccess('/msedcl')) && (
                <NavItem to="/msedcl" icon={<Zap className="w-4 h-4 mr-3 flex-shrink-0" />} label="MSEDCL Bills" testId="nav-msedcl" />
              )}
            </>
          )}
          </>
          )}

          {/* ── LEGAL ── */}
          {(isPartner || isAdmin || canAccess('legal') || userRole.toLowerCase() === 'manager' || dept === 'legal' || customTabs.some(t => ['/legal-cases','/documents','/society-members'].includes(t))) && (
            <>
              <SectionHeader label="Legal" group="legal" />
              {!collapsed.legal && (
                <>
                  {(isPartner || isAdmin || canAccess('legal') || userRole.toLowerCase() === 'manager' || dept === 'legal' || hasCustomAccess('/legal-cases')) && (
                    <NavItem to="/legal-cases" icon={<Scale className="w-4 h-4 mr-3 flex-shrink-0" />} label="Legal Suits" testId="nav-legal-cases" />
                  )}
                  {(isPartner || isAdmin || canAccess('legal') || userRole.toLowerCase() === 'manager' || dept === 'legal' || hasCustomAccess('/legal-cases')) && (
                    <NavItem to="/legal-cases?tab=calendar" icon={<Calendar className="w-4 h-4 mr-3 flex-shrink-0" />} label="Legal Calendar" testId="nav-legal-calendar" />
                  )}
                  {(isPartner || isAdmin || canAccess('legal') || userRole.toLowerCase() === 'manager' || dept === 'legal') && (
                    <NavItem to="/legal-research" icon={<Search className="w-4 h-4 mr-3 flex-shrink-0" />} label="Legal Research" testId="nav-legal-research" />
                  )}
                  {(isPartner || canAccess('documents') || hasCustomAccess('/documents')) && (
                    <NavItem to="/documents" icon={<FileText className="w-4 h-4 mr-3 flex-shrink-0" />} label="Documents" testId="nav-documents" />
                  )}
                  {(isPartner || canAccess('society_members') || hasCustomAccess('/society-members')) && (
                    <NavItem to="/society-members" icon={<Home className="w-4 h-4 mr-3 flex-shrink-0" />} label="Society Members" testId="nav-society-members" />
                  )}
                </>
              )}
            </>
          )}

          {/* ── ADMIN ── */}
          {isAdmin && !isPartner && (
            <>
              <SectionHeader label="Admin" group="adminOps" badge={pendingCounts.generic} />
              {!collapsed.adminOps && (
                <>
                  {/* Vendors & Consultants and Contractor Insurance both moved
                      to the Project section (Feb 2026). Admin now holds only
                      cross-cutting reminder/alert widgets. */}
                  <NavItem to="/activity-reminders" icon={<Bell className="w-4 h-4 mr-3 flex-shrink-0" />} label="Activity Reminders" testId="nav-activity-reminders" />
                </>
              )}
            </>
          )}

          {/* ── ADMINISTRATOR ── */}
          {isAdmin && !isPartner && (
            <>
              <SectionHeader label="Administrator" group="admin" />
              {!collapsed.admin && (
                <>
                  <NavItem to="/online-users" icon={<Wifi className="w-4 h-4 mr-3 flex-shrink-0" />} label="Online Now" testId="nav-online-users" />
                  <NavItem to="/access-matrix" icon={<Grid2X2 className="w-4 h-4 mr-3 flex-shrink-0" />} label="Access Matrix" testId="nav-access-matrix" />
                  <NavItem to="/viewing-matrix" icon={<Shield className="w-4 h-4 mr-3 flex-shrink-0" />} label="Viewing Matrix" testId="nav-viewing-matrix" />
                  <NavItem to="/login-transfer" icon={<ArrowRightLeft className="w-4 h-4 mr-3 flex-shrink-0" />} label="Login Transfer" testId="nav-login-transfer" />
                  <NavItem to="/admin/system" icon={<HeartPulse className="w-4 h-4 mr-3 flex-shrink-0" />} label="System" testId="nav-system" />
                  <NavItem to="/download-management" icon={<Shield className="w-4 h-4 mr-3 flex-shrink-0" />} label="Download Mgmt" testId="nav-download-management" />
                  {/* Deletion Requests — anyone can file a request from
                      the trash icon of a supported entry; admins review
                      and approve/reject here. Introduced Feb 2026. */}
                  <NavItem
                    to="/deletion-requests"
                    icon={<Trash2 className="w-4 h-4 mr-3 flex-shrink-0" />}
                    label="Deletion Requests"
                    testId="nav-deletion-requests"
                  />
                  {/* File Health — admin/director report that flags
                      orphan/zero-size/broken-link records across the top
                      document collections (Jul 2026, user request). */}
                  <NavItem
                    to="/file-health"
                    icon={<HeartPulse className="w-4 h-4 mr-3 flex-shrink-0" />}
                    label="File Health"
                    testId="nav-file-health"
                  />
                  {canAccess('alert_config') && (
                    <NavItem to="/alert-settings" icon={<Settings className="w-4 h-4 mr-3 flex-shrink-0" />} label="Alert Settings" testId="nav-alert-settings" />
                  )}
                  <NavItem to="/teams-webhook" icon={<MessageCircle className="w-4 h-4 mr-3 flex-shrink-0" />} label="MS Teams" testId="nav-teams-webhook" />
                  <NavItem to="/whatsapp" icon={<MessageCircle className="w-4 h-4 mr-3 flex-shrink-0" />} label="WhatsApp Logs" testId="nav-whatsapp" />
                  <NavItem to="/leave-approval-settings" icon={<CalendarCheck className="w-4 h-4 mr-3 flex-shrink-0" />} label="Leave Approvals" testId="nav-leave-approval-settings" />
                  {canAccess('alerts') && (
                    <NavItem to="/alerts" icon={<AlertTriangle className="w-4 h-4 mr-3 flex-shrink-0" />} label="Alerts" testId="nav-alerts" />
                  )}
                </>
              )}
            </>
          )}

          {/* Cloud Storage for non-admin users with manager or custom access */}
          {!isAdmin && (isManager || hasCustomAccess('/storage-dashboard')) && (
            <NavItem to="/storage-dashboard" icon={<Cloud className="w-4 h-4 mr-3 flex-shrink-0" />} label="Cloud Storage" testId="nav-storage-dashboard-custom" />
          )}

          {/* ── SALES ── */}
          <>
          <SectionHeader label="Sales" group="sales" badge={pendingCounts.sales} />
          {!collapsed.sales && (
            <>
              {!isPartner && (isManager || hasCustomAccess('/pre-sales')) && (
                <NavItem to="/pre-sales" icon={<UserPlus className="w-4 h-4 mr-3 flex-shrink-0" />} label="Pre-Sales CRM" testId="nav-pre-sales" />
              )}
              {!isPartner && (isManager || hasCustomAccess('/outdoor-campaigns')) && (
                <NavItem to="/outdoor-campaigns" icon={<Megaphone className="w-4 h-4 mr-3 flex-shrink-0" />} label="Outdoor Campaigns" testId="nav-outdoor-campaigns" />
              )}
              {(isPartner || isManager || hasCustomAccess('/new-purchasers')) && (
                <NavItem to="/new-purchasers" icon={<Users className="w-4 h-4 mr-3 flex-shrink-0" />} label="New Purchasers" testId="nav-new-purchasers" />
              )}
              {!isPartner && (isManager || hasCustomAccess('/birthday-calendar')) && (
                <NavItem to="/birthday-calendar" icon={<Cake className="w-4 h-4 mr-3 flex-shrink-0" />} label="Birthday Calendar" testId="nav-birthday-calendar" />
              )}
              {(isPartner || isManager || hasCustomAccess('/sales-inventory')) && (
                <NavItem to="/sales-inventory" icon={<Package className="w-4 h-4 mr-3 flex-shrink-0" />} label="Sales Inventory" testId="nav-sales-inventory" />
              )}
              {(isPartner || isManager || hasCustomAccess('/sale-approvals')) && (
                <NavItem to="/sale-approvals" icon={<Shield className="w-4 h-4 mr-3 flex-shrink-0" />} label="Sale Approvals" testId="nav-sale-approvals" badge={pendingCounts.sales} />
              )}
              {!isPartner && (isManager || hasCustomAccess('/rera-payment-stages')) && (
                <NavItem to="/rera-payment-stages" icon={<Layers className="w-4 h-4 mr-3 flex-shrink-0" />} label="RERA Payment Stages" testId="nav-rera-payment-stages" />
              )}
              {!isPartner && (isManager || hasCustomAccess('/advertising')) && (
                <NavItem to="/advertising" icon={<Newspaper className="w-4 h-4 mr-3 flex-shrink-0" />} label="Advertising" testId="nav-advertising" />
              )}
              {!isPartner && (isManager || hasCustomAccess('/market-intelligence')) && (
                <NavItem to="/market-intelligence" icon={<TrendingUp className="w-4 h-4 mr-3 flex-shrink-0" />} label="Market Intelligence" testId="nav-market-intelligence" />
              )}
              {!isPartner && (isManager || hasCustomAccess('/competitor-tracker')) && (
                <NavItem to="/competitor-tracker" icon={<Target className="w-4 h-4 mr-3 flex-shrink-0" />} label="Competitor Tracker" testId="nav-competitor-tracker" />
              )}
              {!isPartner && (isManager || hasCustomAccess('/post-sales')) && (
                <NavItem to="/post-sales" icon={<KeyRound className="w-4 h-4 mr-3 flex-shrink-0" />} label="Post-Sales" testId="nav-post-sales" />
              )}
              {(isPartner || isManager || hasCustomAccess('/payment-dashboard')) && (
                <NavItem to="/payment-dashboard" icon={<Wallet className="w-4 h-4 mr-3 flex-shrink-0" />} label="Payment Dashboard" testId="nav-payment-dashboard" />
              )}
              {!isPartner && (isManager || hasCustomAccess('/customer-reviews')) && (
                <NavItem to="/customer-reviews" icon={<Star className="w-4 h-4 mr-3 flex-shrink-0" />} label="Customer Reviews" testId="nav-customer-reviews" />
              )}
              {/* External IGR Link for Stamp Duty — internal only */}
              {!isPartner && (
                <a
                  href="https://gras.mahakosh.gov.in/echallan/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sidebar-link flex items-center"
                  data-testid="nav-igr-stamp-duty"
                >
                  <Landmark className="w-4 h-4 mr-3 flex-shrink-0" />
                  <span>IGR Stamp Duty</span>
                  <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                </a>
              )}
            </>
          )}
          </>
        </nav>

        <div className="p-4 border-t border-slate-700 bg-slate-900 flex-shrink-0">
          <div className="mb-3 px-2">
            <p className="text-white text-sm font-medium truncate">{user?.name}</p>
            <p className="text-slate-500 text-xs uppercase tracking-wide">{user?.role_name || user?.role}</p>
          </div>
          {isAdmin && <ImpersonateDropdown onImpersonate={(token, emp) => {
            // Update React state in place — no full page reload required.
            // This avoids the Cloudflare-strips-Set-Cookie failure mode on
            // production that previously left users at the login page.
            onImpersonate(token, emp);
            navigate('/dashboard/management');
          }} />}
          <NavItem to="/change-password" icon={<KeyRound className="w-4 h-4 mr-3 flex-shrink-0" />} label="Change Password" testId="nav-change-password" />
          <button
            onClick={() => { handleLogout(); handleNavClick(); }}
            className="sidebar-link w-full text-left"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-3 flex-shrink-0" />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;

const ImpersonateDropdown = ({ onImpersonate }) => {
  const [employees, setEmployees] = useState([]);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  // Filter: 'all' | 'employees' | 'partners'
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');

  const loadEmployees = async () => {
    if (employees.length > 0) { setShow(!show); return; }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/employees`, {
        headers: {}
      });
      if (res.ok) setEmployees(await res.json());
    } catch (e) { toast.error("Something went wrong"); }
    setLoading(false);
    setShow(true);
  };

  const handleImpersonate = async (empId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/auth/impersonate/${empId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        const data = await res.json();
        onImpersonate(data.token, data.employee);
      } else {
        // Surface the failure so the user isn't left wondering why nothing
        // happened (previously this silently no-op'd on 401/403/5xx).
        let detail = `Impersonation failed (${res.status})`;
        try {
          const err = await res.json();
          if (err?.detail) detail = String(err.detail);
        } catch { /* response was not JSON */ }
        toast.error(detail);
      }
    } catch (e) { toast.error("Network error during impersonation"); }
  };

  const isPartner = (e) =>
    (e.role_name || '').toLowerCase() === 'partner' || (e.role || '').toLowerCase() === 'partner';

  const filtered = employees
    .filter((e) => e.status === 'active')
    .filter((e) => {
      if (filter === 'partners') return isPartner(e);
      if (filter === 'employees') return !isPartner(e);
      return true;
    })
    .filter((e) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return (
        (e.name || '').toLowerCase().includes(q) ||
        (e.email || '').toLowerCase().includes(q) ||
        (e.department_name || e.department || '').toLowerCase().includes(q)
      );
    });

  const partnerCount = employees.filter((e) => e.status === 'active' && isPartner(e)).length;
  const employeeCount = employees.filter((e) => e.status === 'active' && !isPartner(e)).length;

  const chipCls = (active) =>
    `px-2 py-0.5 rounded-full text-[10px] font-semibold transition-colors ${
      active
        ? 'bg-amber-500 text-slate-900'
        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
    }`;

  return (
    <div className="mb-2 relative" data-testid="impersonate-dropdown">
      <button onClick={loadEmployees}
        className="sidebar-link w-full text-left text-xs text-amber-400 hover:text-amber-300">
        <Users className="w-4 h-4 mr-3 flex-shrink-0" />
        <span>{loading ? 'Loading...' : 'Login As...'}</span>
      </button>
      {show && employees.length > 0 && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-slate-800 rounded-lg border border-slate-600 shadow-xl z-50 flex flex-col max-h-72">
          <div className="p-2 border-b border-slate-700 flex flex-col gap-2">
            <div className="flex items-center gap-1">
              <button
                type="button"
                data-testid="impersonate-filter-all"
                onClick={() => setFilter('all')}
                className={chipCls(filter === 'all')}
              >
                All ({employeeCount + partnerCount})
              </button>
              <button
                type="button"
                data-testid="impersonate-filter-employees"
                onClick={() => setFilter('employees')}
                className={chipCls(filter === 'employees')}
              >
                Employees ({employeeCount})
              </button>
              <button
                type="button"
                data-testid="impersonate-filter-partners"
                onClick={() => setFilter('partners')}
                className={chipCls(filter === 'partners')}
              >
                Partners ({partnerCount})
              </button>
            </div>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, dept…"
              data-testid="impersonate-search"
              className="w-full px-2 py-1 rounded bg-slate-900 border border-slate-700 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-amber-500"
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-slate-500">
                No matches
              </div>
            ) : (
              filtered.map((e) => (
                <button
                  key={e.employee_id}
                  onClick={() => handleImpersonate(e.employee_id)}
                  data-testid={`impersonate-row-${e.employee_id}`}
                  className="w-full text-left px-3 py-2 text-xs text-slate-300 hover:bg-slate-700 hover:text-white transition-colors border-b border-slate-700 last:border-0 flex items-center justify-between gap-2"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="font-medium truncate">{e.name}</span>
                    {isPartner(e) && (
                      <span className="shrink-0 text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 font-semibold uppercase">
                        Partner
                      </span>
                    )}
                  </span>
                  <span className="text-slate-500 shrink-0 truncate max-w-[45%] text-right">
                    {e.department_name || e.department}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
